import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { GET, PUT } from '../../../configs/api';
import { api } from '../../../utils/constants';

const CHANNEL_ORDER = ['in_app', 'email', 'whatsapp'];

const prefKey = (eventType, channel) => `${eventType}:${channel}`;

/**
 * Per-company notification channel toggles. Auto-saves each change.
 * WhatsApp is shown as coming soon (prefs stored when enabled later).
 */
export default function NotificationPreferencesSection({ isDarkMode }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saveError, setSaveError] = React.useState(null);
  const [eventTypes, setEventTypes] = React.useState([]);
  const [eventLabels, setEventLabels] = React.useState({});
  const [channelLabels, setChannelLabels] = React.useState({});
  const [prefs, setPrefs] = React.useState({});
  const [savingKeys, setSavingKeys] = React.useState(() => new Set());
  const prefsRef = React.useRef(prefs);
  prefsRef.current = prefs;

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await GET(api.notificationPreferences);
    if (res.error) {
      setError(res.message || 'Failed to load notification preferences');
      setLoading(false);
      return;
    }
    const data = res.data || {};
    const next = {};
    for (const row of data.preferences || []) {
      next[prefKey(row.event_type, row.channel)] = row.enabled !== false;
    }
    setPrefs(next);
    setEventTypes(Array.isArray(data.event_types) ? data.event_types : []);
    setEventLabels(data.event_type_labels || {});
    setChannelLabels(data.channel_labels || {});
    setLoading(false);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const setSaving = (key, on) => {
    setSavingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleToggle = async (eventType, channel, enabled) => {
    if (channel === 'whatsapp') return;

    const key = prefKey(eventType, channel);
    const previous = prefsRef.current[key] !== false;
    setSaveError(null);
    setPrefs((p) => ({ ...p, [key]: enabled }));
    setSaving(key, true);

    const res = await PUT(api.notificationPreferences, {}, {
      preferences: [{ event_type: eventType, channel, enabled }],
    });

    setSaving(key, false);

    if (res.error) {
      setPrefs((p) => ({ ...p, [key]: previous }));
      setSaveError(res.message || 'Could not save preference');
      return;
    }

    const data = res.data || {};
    if (Array.isArray(data.preferences)) {
      const next = {};
      for (const row of data.preferences) {
        next[prefKey(row.event_type, row.channel)] = row.enabled !== false;
      }
      setPrefs(next);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  if (eventTypes.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        No notification types apply to your role in this company.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8125rem' }}>
        Choose how you want to hear about each event you can receive in this company.
        Changes save automatically. In app and email default on; WhatsApp is coming soon.
      </Typography>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: 'minmax(0, 1.4fr) repeat(3, minmax(88px, 1fr))',
          gap: 1,
          px: 1,
          mb: 1,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
          Event
        </Typography>
        {CHANNEL_ORDER.map((ch) => (
          <Typography
            key={ch}
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}
          >
            {channelLabels[ch] || ch}
            {ch === 'whatsapp' ? ' · Soon' : ''}
          </Typography>
        ))}
      </Box>

      {eventTypes.map((eventType, index) => (
        <React.Fragment key={eventType}>
          {index > 0 && (
            <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#e0e0e0' }} />
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(0, 1.4fr) repeat(3, minmax(88px, 1fr))',
              },
              gap: { xs: 1, md: 1 },
              alignItems: 'center',
              py: 1.5,
              px: 1,
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
              {eventLabels[eventType] || eventType}
            </Typography>

            {CHANNEL_ORDER.map((channel) => {
              const key = prefKey(eventType, channel);
              const enabled = prefs[key] !== false;
              const isWhatsApp = channel === 'whatsapp';
              const busy = savingKeys.has(key);

              const control = (
                <FormControlLabel
                  sx={{
                    m: 0,
                    justifyContent: { xs: 'space-between', md: 'center' },
                    width: { xs: '100%', md: 'auto' },
                    mr: { xs: 0, md: 0 },
                  }}
                  label={
                    <Typography
                      variant="caption"
                      sx={{ display: { xs: 'inline', md: 'none' }, color: 'text.secondary' }}
                    >
                      {channelLabels[channel] || channel}
                    </Typography>
                  }
                  labelPlacement="start"
                  control={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {busy && <CircularProgress size={14} />}
                      <Switch
                        size="small"
                        checked={isWhatsApp ? false : enabled}
                        disabled={isWhatsApp || busy}
                        onChange={(e) => handleToggle(eventType, channel, e.target.checked)}
                        inputProps={{
                          'aria-label': `${eventLabels[eventType] || eventType} ${channelLabels[channel] || channel}`,
                        }}
                      />
                    </Box>
                  }
                />
              );

              if (isWhatsApp) {
                return (
                  <Tooltip key={channel} title="WhatsApp notifications coming soon" placement="top">
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'center' } }}>
                      {control}
                      <Chip
                        label="Soon"
                        size="small"
                        sx={{
                          display: { xs: 'none', md: 'inline-flex' },
                          ml: 0.5,
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: isDarkMode ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.06)',
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <Box
                  key={channel}
                  sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'center' } }}
                >
                  {control}
                </Box>
              );
            })}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}
